import type * as THREE from 'three';
import { isTouch } from '../core/device';
import { currentControls } from '../core/Gamepads';
import type { MiniResult } from '../minigames/types';
import type { Stage, StageButton, StageHud, StageInput } from '../stages/types';
import { clear, h } from './dom';
import { setRich } from '../icons/dom';
import { plainText } from '../icons/rich';

/** What to show when it's over; null closes straight back to the game. */
type Finish = (stage: Stage, bailed: boolean) => MiniResult | null;

const ACTION_KEYS = ['Space', 'KeyE', 'Enter', 'NumpadEnter'];
/** A press that moves less than this (pixels) is a click, not a drag. */
const CLICK_SLOP = 8;

/**
 * Runs in-world mini-games (stages): the bull, the rifle, the dartboard. Unlike the
 * cardboard mini-game panel, the 3D view stays up; this draws a light HUD on top,
 * turns mouse and touch into aim and taps, and shows the result card when it's over.
 */
export class StageHost {
  private readonly root: HTMLDivElement;
  private readonly top: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly scoreEl: HTMLDivElement;
  private readonly statusEl: HTMLDivElement;
  private readonly bannerEl: HTMLDivElement;
  private readonly meters: HTMLDivElement;
  private readonly hintEl: HTMLDivElement;
  private readonly buttonsEl: HTMLDivElement;
  private readonly crosshair: HTMLDivElement;
  private readonly scopeEl: HTMLDivElement;
  private readonly lockNote: HTMLDivElement;
  private readonly flashEl: HTMLDivElement;
  private readonly result: HTMLDivElement;
  private readonly card: HTMLDivElement;
  private stage: Stage | null = null;
  private finish: Finish | null = null;
  private phase: 'closed' | 'playing' | 'result' = 'closed';
  private resultAt = 0;
  // Input gathered between frames.
  private held = false;
  private pressed = false;
  private released = false;
  private fire = false;
  private alt = false;
  private keys: string[] = [];
  private readonly down = new Set<string>();
  private lookX = 0;
  private lookY = 0;
  private tap: { x: number; y: number } | null = null;
  private drag: { id: number; x: number; y: number; moved: number } | null = null;
  private hudKey = '';
  /** Flip vertical aim (the Invert Y setting). */
  invertY = false;

  constructor(
    parent: HTMLElement,
    private readonly canvas: HTMLCanvasElement,
    private readonly hooks: { onOpen(): void; onClose(): void },
  ) {
    this.titleEl = h('div', { class: 'stage-title' });
    this.scoreEl = h('div', { class: 'stage-score' });
    this.statusEl = h('div', { class: 'stage-status' });
    this.top = h('div', { class: 'stage-top' }, this.titleEl, this.scoreEl, this.statusEl);
    this.bannerEl = h('div', { class: 'stage-banner', hidden: true });
    this.meters = h('div', { class: 'stage-meters' });
    this.hintEl = h('div', { class: 'stage-hint' });
    this.buttonsEl = h('div', { class: 'stage-buttons' });
    this.crosshair = h('div', { class: 'stage-crosshair', hidden: true });
    this.scopeEl = h('div', { class: 'stage-scope', hidden: true });
    this.lockNote = h('div', { class: 'stage-locknote', hidden: true, text: 'Click to aim' });
    this.flashEl = h('div', { class: 'stage-flash', hidden: true });
    this.card = h('div', { class: 'minigame cardboard', attrs: { role: 'dialog', 'aria-modal': 'true' } });
    this.result = h('div', { class: 'layer interactive', hidden: true }, this.card);
    this.root = h(
      'div',
      { class: 'stage', hidden: true },
      this.scopeEl,
      this.flashEl,
      this.crosshair,
      this.top,
      this.bannerEl,
      this.lockNote,
      this.meters,
      this.hintEl,
      this.buttonsEl,
    );
    parent.append(this.root, this.result);
    window.addEventListener('keydown', this.onKeyDown, true);
    window.addEventListener('keyup', this.onKeyUp, true);
    canvas.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    document.addEventListener('pointerlockchange', this.onLockChange);
  }

  /** A stage is running or its result card is up (the game treats either as a menu). */
  get isOpen(): boolean {
    return this.phase !== 'closed';
  }

  /** A stage is running and owns the camera. */
  get playing(): boolean {
    return this.phase === 'playing' && this.stage !== null;
  }

  open(stage: Stage, finish: Finish): void {
    if (this.phase === 'closed') this.hooks.onOpen();
    // Never leave a half-finished stage holding the player or the camera.
    if (this.phase === 'playing') this.stage?.end(true);
    this.stage = stage;
    this.finish = finish;
    this.phase = 'playing';
    this.resetInput();
    // Last game's meters must not linger: force a rebuild on the first HUD update.
    this.hudKey = '-';
    clear(this.meters);
    setRich(this.titleEl, stage.title);
    setRich(this.hintEl, stage.hint(currentControls()));
    this.renderButtons(stage.buttons(isTouch()));
    this.crosshair.hidden = !stage.crosshair;
    this.root.hidden = false;
    this.result.hidden = true;
    stage.begin();
    if (stage.pointerLock && !isTouch()) this.requestLock();
  }

  /** Called every frame by the game loop. */
  update(dt: number): void {
    const stage = this.stage;
    if (this.phase !== 'playing' || !stage) return;
    const input: StageInput = {
      held: this.held,
      pressed: this.pressed,
      released: this.released,
      keys: this.keys,
      down: this.down,
      lookX: this.lookX,
      lookY: this.invertY ? -this.lookY : this.lookY,
      tap: this.tap,
      fire: this.fire,
      alt: this.alt,
    };
    this.pressed = this.released = this.fire = false;
    this.tap = null;
    this.keys = [];
    this.lookX = this.lookY = 0;
    stage.update(dt, input);
    this.renderHud(stage.hud());
    const hint = stage.hint(currentControls());
    setRich(this.hintEl, hint);
    const scope = stage.scope ?? 0;
    this.scopeEl.hidden = scope < 0.05;
    if (!this.scopeEl.hidden) this.scopeEl.style.opacity = String(Math.min(1, scope * 1.3));
    this.crosshair.hidden = !stage.crosshair || scope > 0.5;
    this.lockNote.hidden = !stage.pointerLock || isTouch() || this.locked;
    const flash = stage.whiteout ?? 0;
    this.flashEl.hidden = flash < 0.01;
    if (!this.flashEl.hidden) this.flashEl.style.opacity = String(Math.min(1, flash));
    if (stage.done) this.end(false);
  }

  /** Aim from a game controller's stick, in mouse-pixel equivalents. */
  look(dx: number, dy: number): void {
    if (this.phase !== 'playing') return;
    this.lookX += dx;
    this.lookY += dy;
  }

  /** The stage points the camera while it runs. */
  frame(camera: THREE.PerspectiveCamera, dt: number): void {
    if (this.phase === 'playing') this.stage?.frame(camera, dt);
  }

  private get locked(): boolean {
    return document.pointerLockElement === this.canvas;
  }

  private requestLock(): void {
    try {
      const r = this.canvas.requestPointerLock() as unknown;
      if (r instanceof Promise) r.catch(() => undefined);
    } catch {
      // No pointer lock (iframe, old browser): dragging to aim still works.
    }
  }

  private releaseLock(): void {
    if (this.locked) document.exitPointerLock();
  }

  private bail(): void {
    if (this.phase === 'playing') this.end(true);
    else this.close();
  }

  private end(bailed: boolean): void {
    const stage = this.stage;
    const finish = this.finish;
    this.releaseLock();
    this.root.hidden = true;
    if (!stage || !finish) return this.close();
    stage.end(bailed);
    const r = finish(stage, bailed);
    if (!r) {
      this.phase = 'result';
      this.close();
      return;
    }
    this.phase = 'result';
    this.resultAt = performance.now();
    clear(this.card);
    const ok = h('button', { class: 'tape primary', text: 'Aight', on: { click: () => this.close() } });
    this.card.append(
      h('h2', { class: 'minigame-title', text: r.title }),
      h('div', { class: 'minigame-art', text: r.art }),
      h('div', { class: 'modal-text', text: r.text }),
      h('div', { class: 'row' }, ok),
    );
    this.result.hidden = false;
    ok.focus({ preventScroll: true });
  }

  close(): void {
    if (this.phase === 'closed') return;
    if (this.phase === 'playing') this.stage?.end(true);
    this.releaseLock();
    this.phase = 'closed';
    this.root.hidden = true;
    this.result.hidden = true;
    this.stage = null;
    this.finish = null;
    this.resetInput();
    this.hooks.onClose();
  }

  private resetInput(): void {
    this.held = this.pressed = this.released = this.fire = this.alt = false;
    this.keys = [];
    this.down.clear();
    this.lookX = this.lookY = 0;
    this.tap = null;
    this.drag = null;
  }

  // --- HUD -------------------------------------------------------------------------

  private renderHud(hud: StageHud): void {
    setRich(this.scoreEl, hud.score ?? '');
    setRich(this.statusEl, hud.status ?? '');
    const b = hud.banner;
    this.bannerEl.hidden = !b;
    if (b) {
      setRich(this.bannerEl, b.text);
      this.bannerEl.style.color = b.color ?? '#ffd400';
    }
    const meters = hud.meters ?? [];
    // Rebuild the meter rows only when their shape changes; values just move bars.
    const key = meters.map((m) => `${m.label}|${m.marker ? 1 : 0}|${m.zone ? 1 : 0}`).join(',');
    if (key !== this.hudKey) {
      this.hudKey = key;
      clear(this.meters);
      for (const m of meters) {
        this.meters.append(
          h(
            'div',
            { class: 'stage-meter' },
            h('span', { class: 'stage-meter-label', text: m.label }),
            h(
              'div',
              { class: 'stage-meter-bar' },
              h('div', { class: 'stage-meter-zone' }),
              h('div', { class: m.marker ? 'stage-meter-marker' : 'stage-meter-fill' }),
            ),
          ),
        );
      }
    }
    meters.forEach((m, i) => {
      const row = this.meters.children[i] as HTMLElement | undefined;
      if (!row) return;
      const zone = row.querySelector<HTMLElement>('.stage-meter-zone');
      const val = row.querySelector<HTMLElement>(m.marker ? '.stage-meter-marker' : '.stage-meter-fill');
      const v = Math.max(0, Math.min(1, m.value));
      if (zone) {
        zone.hidden = !m.zone;
        if (m.zone) {
          zone.style.left = `${m.zone[0] * 100}%`;
          zone.style.width = `${Math.max(0, m.zone[1] - m.zone[0]) * 100}%`;
        }
      }
      if (val) {
        if (m.marker) val.style.left = `${v * 100}%`;
        else val.style.width = `${v * 100}%`;
        val.style.background = m.color ?? '';
      }
    });
  }

  private renderButtons(buttons: StageButton[]): void {
    clear(this.buttonsEl);
    for (const b of buttons) {
      const btn = h('button', {
        class: `stage-btn interactive${b.primary ? ' primary' : ''}`,
        text: b.label,
        attrs: { 'aria-label': plainText(b.label, true) },
      });
      let on = false;
      const press = (): void => {
        if (b.code === 'Space') {
          if (!this.held) this.pressed = true;
          this.held = true;
          this.fire = true;
          this.keys.push('Space');
        } else if (b.code === 'Alt') this.alt = b.toggle ? !this.alt : true;
        else {
          this.keys.push(b.code);
          this.down.add(b.code);
        }
      };
      const release = (): void => {
        if (b.code === 'Space') {
          if (this.held) this.released = true;
          this.held = false;
        } else if (b.code === 'Alt') {
          if (!b.toggle) this.alt = false;
        } else this.down.delete(b.code);
      };
      btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (b.toggle) {
          on = !on;
          btn.classList.toggle('on', on);
          if (b.code === 'Alt') this.alt = on;
          else if (on) press();
          else release();
          return;
        }
        press();
      });
      if (!b.toggle) {
        btn.addEventListener('pointerup', release);
        btn.addEventListener('pointercancel', release);
        btn.addEventListener('pointerleave', release);
      }
      this.buttonsEl.append(btn);
    }
    this.buttonsEl.append(
      h('button', {
        class: 'stage-btn interactive bail',
        text: ':runner: Bail',
        on: {
          click: (e) => {
            e.stopPropagation();
            this.bail();
          },
        },
      }),
    );
  }

  // --- input ----------------------------------------------------------------------

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (this.phase !== 'playing') return;
    e.preventDefault();
    if (e.button === 2) {
      this.alt = true;
      return;
    }
    if (this.stage?.pointerLock && !isTouch() && !this.locked) {
      // Clicking grabs the mouse. If the browser says no, the click still counts.
      this.requestLock();
    } else if (this.locked) {
      this.fire = true;
      if (!this.held) this.pressed = true;
      this.held = true;
      return;
    }
    this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: 0 };
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (this.phase !== 'playing') return;
    if (this.locked) {
      this.lookX += e.movementX;
      this.lookY += e.movementY;
      return;
    }
    const d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    d.moved += Math.hypot(dx, dy);
    d.x = e.clientX;
    d.y = e.clientY;
    const k = e.pointerType === 'touch' ? 1.3 : 1;
    this.lookX += dx * k;
    this.lookY += dy * k;
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    if (e.button === 2) {
      this.alt = false;
      return;
    }
    if (this.locked && this.held) {
      this.held = false;
      this.released = true;
    }
    const d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    this.drag = null;
    if (this.phase !== 'playing' || d.moved > CLICK_SLOP) return;
    const r = this.canvas.getBoundingClientRect();
    this.tap = {
      x: ((e.clientX - r.left) / r.width) * 2 - 1,
      y: -(((e.clientY - r.top) / r.height) * 2 - 1),
    };
    this.fire = true;
  };

  private readonly onLockChange = (): void => {
    if (!this.locked) this.held = false;
    // The click that grabbed the mouse doesn't also pull the trigger.
    else this.drag = null;
  };

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (this.phase === 'closed') return;
    e.stopImmediatePropagation();
    if (this.phase === 'result') {
      if (performance.now() - this.resultAt > 400 && (ACTION_KEYS.includes(e.code) || e.key === 'Escape')) {
        e.preventDefault();
        this.close();
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      this.bail();
      return;
    }
    if (ACTION_KEYS.includes(e.code)) {
      e.preventDefault();
      if (!e.repeat && !this.held) {
        this.pressed = true;
        this.fire = true;
      }
      // Stages that care which button it was (Space vs E) can look it up.
      if (!e.repeat) this.keys.push(e.code);
      this.held = true;
      return;
    }
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.code)) e.preventDefault();
    if (!e.repeat) this.keys.push(e.code);
    this.down.add(e.code);
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    if (this.phase === 'closed') return;
    if (ACTION_KEYS.includes(e.code)) {
      if (this.held) this.released = true;
      this.held = false;
      return;
    }
    this.down.delete(e.code);
  };
}
